/**
 * Leviathan Web - Sales Report Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Sales report by product, customer, and time period
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  RefreshCw,
  Printer,
  DollarSign,
  FileText,
  Fuel,
  TrendingUp,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface SalesReport {
  dateRange: { startDate: string; endDate: string };
  generatedAt: string;
  summary: {
    totalInvoices: number;
    totalSales: number;
    totalTax: number;
    totalGallons: number;
    averageInvoice: number;
  };
  byProduct: Array<{
    productId: string;
    sku: string;
    name: string;
    quantity: number;
    revenue: number;
  }>;
  byCustomer: Array<{
    customerId: string;
    customerNumber: string;
    name: string;
    invoiceCount: number;
    revenue: number;
  }>;
  byDate: Array<{ date: string; invoiceCount: number; total: number }>;
}

/**
 * Stat card component
 */
function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="relative bg-panel-dark border border-panel-border p-4">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="flex items-center space-x-2 mb-2">
        <span className="text-amber">{icon}</span>
        <span className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
          {label}
        </span>
      </div>
      <div
        className="text-xl font-mono font-bold text-amber"
        style={{ textShadow: '0 0 10px rgba(255,149,0,0.6)' }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Sales Report Page
 */
const SalesReport = () => {
  const { accessToken } = useAuth();
  const [report, setReport] = useState<SalesReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'summary' | 'products' | 'customers' | 'timeline'>(
    'summary'
  );

  // Default to current month
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate]);

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reports/sales?startDate=${startDate}&endDate=${endDate}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setReport(data.report);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      amount
    );
  };

  const setQuickRange = (range: string) => {
    const now = new Date();
    let start: Date;
    const end: Date = now;

    switch (range) {
      case 'today':
        start = now;
        break;
      case 'week':
        start = new Date(now);
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/reports"
            className="p-2 text-text-tertiary hover:text-amber transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-mono uppercase tracking-wider">
              Sales Report
            </h1>
            <p className="text-sm text-text-secondary font-mono mt-1">
              Revenue analysis by product, customer, and time
            </p>
          </div>
        </div>
        <button
          onClick={() => window.print()}
          className="flex items-center space-x-2 px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors"
        >
          <Printer className="w-4 h-4" />
          <span className="font-mono text-sm">Print</span>
        </button>
      </div>

      {/* Date Range Controls */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-background border border-panel-border focus:border-amber px-4 py-2 text-text-primary font-mono focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-background border border-panel-border focus:border-amber px-4 py-2 text-text-primary font-mono focus:outline-none"
            />
          </div>
          <div className="flex gap-2">
            {['today', 'week', 'month', 'quarter', 'year'].map((range) => (
              <button
                key={range}
                onClick={() => setQuickRange(range)}
                className="bg-background border border-panel-border hover:border-amber/50 px-3 py-2 text-text-secondary hover:text-amber font-mono text-xs uppercase transition-colors"
              >
                {range}
              </button>
            ))}
          </div>
          <button
            onClick={fetchReport}
            disabled={isLoading}
            className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-amber/80 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            label="Total Sales"
            value={formatCurrency(report.summary.totalSales)}
            icon={<DollarSign className="w-5 h-5" />}
          />
          <StatCard
            label="Invoices"
            value={report.summary.totalInvoices.toString()}
            icon={<FileText className="w-5 h-5" />}
          />
          <StatCard
            label="Total Gallons"
            value={`${report.summary.totalGallons.toLocaleString()} gal`}
            icon={<Fuel className="w-5 h-5" />}
          />
          <StatCard
            label="Avg Invoice"
            value={formatCurrency(report.summary.averageInvoice)}
            icon={<TrendingUp className="w-5 h-5" />}
          />
          <StatCard
            label="Total Tax"
            value={formatCurrency(report.summary.totalTax)}
            icon={<DollarSign className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-panel-border">
        {[
          { id: 'summary', label: 'Summary' },
          { id: 'products', label: 'By Product' },
          { id: 'customers', label: 'By Customer' },
          { id: 'timeline', label: 'Timeline' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`px-4 py-2 font-mono text-sm uppercase transition-colors ${
              activeTab === tab.id
                ? 'text-amber border-b-2 border-amber'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6">
          {isLoading ? (
            <div className="text-center py-12 text-text-tertiary font-mono">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading report...
            </div>
          ) : !report ? (
            <div className="text-center py-12 text-text-tertiary font-mono">
              No data available
            </div>
          ) : (
            <>
              {activeTab === 'summary' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-mono text-text-primary uppercase tracking-wide mb-4">
                      Top Products
                    </h3>
                    <div className="space-y-2">
                      {report.byProduct.slice(0, 5).map((product) => (
                        <div
                          key={product.productId}
                          className="flex justify-between items-center p-3 bg-background border border-panel-border"
                        >
                          <div>
                            <span className="text-text-primary font-mono">{product.name}</span>
                            <span className="text-text-tertiary text-xs ml-2">
                              {product.quantity.toLocaleString()} qty
                            </span>
                          </div>
                          <span className="text-amber font-mono font-semibold">
                            {formatCurrency(product.revenue)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-mono text-text-primary uppercase tracking-wide mb-4">
                      Top Customers
                    </h3>
                    <div className="space-y-2">
                      {report.byCustomer.slice(0, 5).map((customer) => (
                        <div
                          key={customer.customerId}
                          className="flex justify-between items-center p-3 bg-background border border-panel-border"
                        >
                          <div>
                            <span className="text-text-primary font-mono">{customer.name}</span>
                            <span className="text-text-tertiary text-xs ml-2">
                              {customer.invoiceCount} inv
                            </span>
                          </div>
                          <span className="text-amber font-mono font-semibold">
                            {formatCurrency(customer.revenue)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'products' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-panel-border">
                      <tr>
                        <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Product
                        </th>
                        <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Quantity
                        </th>
                        <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Revenue
                        </th>
                        <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          % of Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byProduct.map((product) => (
                        <tr
                          key={product.productId}
                          className="border-b border-panel-border/50 hover:bg-amber/5"
                        >
                          <td className="px-4 py-3">
                            <div className="text-text-primary font-mono">{product.name}</div>
                            <div className="text-text-tertiary text-xs font-mono">
                              {product.sku}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-text-secondary font-mono">
                            {product.quantity.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-amber font-mono font-semibold">
                            {formatCurrency(product.revenue)}
                          </td>
                          <td className="px-4 py-3 text-right text-text-secondary font-mono">
                            {((product.revenue / report.summary.totalSales) * 100).toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'customers' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-panel-border">
                      <tr>
                        <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Customer
                        </th>
                        <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Invoices
                        </th>
                        <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Revenue
                        </th>
                        <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Avg Invoice
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byCustomer.map((customer) => (
                        <tr
                          key={customer.customerId}
                          className="border-b border-panel-border/50 hover:bg-amber/5"
                        >
                          <td className="px-4 py-3">
                            <Link
                              to={`/customers/${customer.customerId}`}
                              className="text-amber hover:text-amber/80 font-mono"
                            >
                              {customer.name}
                            </Link>
                            <div className="text-text-tertiary text-xs font-mono">
                              {customer.customerNumber}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-text-secondary font-mono">
                            {customer.invoiceCount}
                          </td>
                          <td className="px-4 py-3 text-right text-amber font-mono font-semibold">
                            {formatCurrency(customer.revenue)}
                          </td>
                          <td className="px-4 py-3 text-right text-text-secondary font-mono">
                            {formatCurrency(customer.revenue / customer.invoiceCount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-panel-border">
                      <tr>
                        <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Date
                        </th>
                        <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Invoices
                        </th>
                        <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase font-mono">
                          Revenue
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byDate.map((row) => (
                        <tr
                          key={row.date}
                          className="border-b border-panel-border/50 hover:bg-amber/5"
                        >
                          <td className="px-4 py-3 text-text-primary font-mono">{row.date}</td>
                          <td className="px-4 py-3 text-right text-text-secondary font-mono">
                            {row.invoiceCount}
                          </td>
                          <td className="px-4 py-3 text-right text-amber font-mono font-semibold">
                            {formatCurrency(row.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Report Info */}
      {report && (
        <div className="text-center text-text-tertiary font-mono text-xs">
          Report generated: {new Date(report.generatedAt).toLocaleString()} | Period:{' '}
          {new Date(report.dateRange.startDate).toLocaleDateString()} -{' '}
          {new Date(report.dateRange.endDate).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default SalesReport;
